// De AFSPRAKEN-lane vullen vanuit Outlook in plaats van vanuit Airtable.
//
// Waarom: de capaciteitsrij rekent al met de échte agenda-uren uit Graph, maar de blokken
// kwamen uit de Airtable-sync (`Type = 'Afspraak'`). Die twee lopen uiteen, en dat is
// precies de verwarring die je op het scherm ziet: op 18 augustus stond 8,75 uur agenda
// tegenover 4,75 uur aan afspraakblokken — het percentage was niet te herleiden tot wat
// eronder stond. Nu komen blok én uur uit dezelfde Graph-sweep, dus wat je ziet telt op.
//
// Fail-soft is hier belangrijker dan volledig: als Graph niets teruggeeft voor iemand
// (geen mailbox, geen toegang, throttling), blijven diens Airtable-afspraken staan. Een
// lege agenda tonen terwijl er wél afspraken zijn is erger dan een verouderd blok.

const AFSPRAAK_TYPE = 'Afspraak';

/** Sleutel die stabiel blijft tussen twee ophaalrondes, zodat React-keys niet springen. */
const eventId = (naam, ev) =>
  `outlook:${naam}:${ev.date}:${ev.start_time || ''}:${ev.subject || ''}`;

/**
 * Eén Graph-event als taak-object, in dezelfde vorm die de rest van het scherm verwacht.
 *
 * `hours: 0` is opzet, geen luiheid: de agenda-uren tellen al via `agendaUren` mee in
 * `computeDayLoad`. Zou het blok óók uren dragen, dan telde elke meeting dubbel.
 */
export function eventNaarTaak(naam, ev) {
  return {
    id: eventId(naam, ev),
    // Bij een privé-afspraak is `subject` al in de engine vervangen door "Privé"; het
    // echte onderwerp bereikt de browser niet. `prive` is er alleen om het slotje te
    // tekenen, zodat duidelijk is dat er niets ontbreekt maar iets is afgeschermd.
    name: ev.subject || 'Afspraak',
    prive: Boolean(ev.prive),
    // Wie er aanschuift, bepaald door de engine op de deelnemerslijst: 'taak' (niemand
    // anders — dus geblokte werktijd), 'intern' of 'extern'. Het scherm kleurt hierop.
    afspraakSoort: ev.soort || 'intern',
    // Titel is geredigeerd (privé of een gevoelig onderwerp) — verklaart het slotje.
    afgeschermd: Boolean(ev.afgeschermd ?? ev.prive),
    type: AFSPRAAK_TYPE,
    prio: AFSPRAAK_TYPE,
    hours: 0,
    startDate: ev.date,
    endDate: ev.date,
    deadline: '',
    deadlineType: '',
    opmerking: '',
    project: '',
    projectNumber: '',
    client: '',
    projectName: '',
    projectleider: '',
    assignee: naam,
    assignees: [naam],
    isComplete: false,
    startTime: ev.start_time || '',
    endTime: ev.end_time || '',
    // Vlag voor de schrijfkant: dit blok bestaat niet in Airtable en mag nooit een PATCH
    // krijgen. Het id is geen record-id; zonder deze vlag is dat verderop niet te zien.
    uitOutlook: true,
  };
}

/**
 * Vervang per persoon de Airtable-afspraken door de Outlook-agenda.
 *
 * @param tasksByAssignee  { naam: taak[] } zoals /Api/dagstart het teruggeeft
 * @param eventsPerNaam    { naam: graph-event[] } — ontbrekend of leeg = niets vervangen
 */
export function metOutlookAfspraken(tasksByAssignee, eventsPerNaam) {
  if (!eventsPerNaam || !Object.keys(eventsPerNaam).length) return tasksByAssignee || {};

  const uit = {};
  Object.entries(tasksByAssignee || {}).forEach(([naam, taken]) => {
    const events = eventsPerNaam[naam];
    // `undefined` = niet opgehaald of mislukt → Airtable houden. Een lege array is wél
    // een antwoord ("deze week niets"), en dan hoort de lane leeg te zijn.
    if (!Array.isArray(events)) { uit[naam] = taken; return; }
    const werk = (taken || []).filter((t) => t.type !== AFSPRAAK_TYPE);
    uit[naam] = [...events.map((ev) => eventNaarTaak(naam, ev)), ...werk];
  });

  // Iemand kan afspraken hebben zonder ook maar één taak in het venster; die valt anders
  // van het scherm omdat /Api/dagstart hem niet noemt.
  Object.entries(eventsPerNaam).forEach(([naam, events]) => {
    if (!uit[naam] && Array.isArray(events) && events.length) {
      uit[naam] = events.map((ev) => eventNaarTaak(naam, ev));
    }
  });

  return uit;
}
